import React, { useCallback, useMemo } from 'react';
import type { Action, ActionFn, SetIsLoading, Filter } from '#/common/components/DataGrid';
import DataGrid from '#/common/components/DataGrid';
import type { Expense } from '#/graphql/types';
import columns from './columns';
import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getExpenseList } from '#/graphql/queries';
import useExpenseDelete from '../../hooks/useExpenseDelete';
import useToast from '#/common/hooks/useToast';
import { useSearchParams } from 'react-router-dom';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';

const getExpenseListQuery = gql(getExpenseList);

export interface ExpenseDataGridProps {
  omitColumns?: string[];
  permanentFilters?: Filter[];
  hideNewAction?: boolean;
}

const ExpenseDataGrid: React.FC<ExpenseDataGridProps> = ({
  omitColumns,
  permanentFilters,
  hideNewAction,
}) => {
  const { confirm } = useToast();
  const { deleteExpense } = useExpenseDelete();

  const [searchParams] = useSearchParams();
  const workOrderId =
    searchParams.get('workOrderId') ||
    getPermanentFilterValue('workOrderId', permanentFilters) ||
    undefined;
  const tenantId =
    searchParams.get('tenantId') ||
    getPermanentFilterValue('tenantId', permanentFilters) ||
    undefined;

  const deleteItem = useCallback(
    async (selectedItems: Expense[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!selectedItems || selectedItems.length === 0) {
        return undefined;
      }

      // Filter items that can be deleted (only "draft" status)
      const deletableItems = selectedItems.filter((item: Expense) => item.status === 'draft');
      const nonDeletableItems = selectedItems.filter((item: Expense) => item.status !== 'draft');

      let confirmMessage = '';
      if (nonDeletableItems.length > 0) {
        confirmMessage =
          confirmMessage = `${deletableItems.length} of ${selectedItems.length} selected expenses will be deleted. Only expenses with "Draft" status can be deleted. Continue?`;
      } else {
        confirmMessage = `Are you sure you want to delete ${deletableItems.length} expense${deletableItems.length > 1 ? 's' : ''}?`;
      }

      const sure = await confirm('error', confirmMessage);
      if (!sure) {
        return;
      }

      setIsLoading && setIsLoading(true, 'Deleting data...');
      // Delete only the deletable items
      const idsToDelete = deletableItems.map((item: Expense) => item.id);
      await deleteExpense(idsToDelete.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteExpense, confirm, alert],
  );

  const userSelectedActions: ActionFn<Expense>[] = [
    {
      key: 'delete-expenses',
      icon: 'trash',
      text: 'Delete',
      disabled: (e) => {
        const filteredKeysForDelete = e.selectedItems.filter((i: any) => i.status === 'draft');
        let count = filteredKeysForDelete?.length;
        return count < 1;
      },
      onClick: async (e) => {
        await deleteItem(e.selectedItems, { setIsLoading: e.setIsLoading });
        e.dg?.instance.clearSelection();
        e.dg?.instance.refresh();
      },
    },
  ];

  const cols = useMemo(
    () => columns.filter((c) => !(omitColumns || []).includes(c.dataField)),
    [omitColumns],
  );

  return (
    <DataGrid
      id="expenses"
      pluralText="Expenses"
      singularText="Expense"
      columns={cols}
      recordUrl="/expenses/:id"
      actions={actions}
      getItemsQuery={getExpenseListQuery}
      selectedActions={userSelectedActions}
      permanentFilters={permanentFilters}
    />
  );
};

export default React.memo(ExpenseDataGrid);
